import type { PartialDeep } from "type-fest";
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";
import * as z from "zod";
import { useInternalFormApi, type GenericValues, type InternalFormApiId } from "./form-context";

export type AnySchema = z.ZodTypeAny;

type Numeric = { valueOf(): number };
type Rule<TValues, TRule> =
	| TRule
	| ComputedRef<TRule | undefined>
	| ((values: PartialDeep<TValues>) => TRule | undefined)
	| undefined;

type BuiltInRule<TValues> = {
	isRequired?: Rule<TValues, boolean>;
	isEmail?: Rule<TValues, boolean>;
	isNumber?: Rule<TValues, boolean>;
	isDate?: Rule<TValues, boolean>;
	min?: Rule<TValues, Numeric>;
	max?: Rule<TValues, Numeric>;
	errorMap?: z.ZodErrorMap;
};

type SchemaRule<TValues> = {
	schema: Rule<TValues, z.ZodTypeAny | undefined>;
};

type GranularRule<TValues> = {
	asyncDebounceMs: number;
	validators: {
		onChange?: Rule<TValues, z.ZodTypeAny | undefined>;
		onChangeAsync?: Rule<TValues, z.ZodTypeAny | undefined>;

		onBlur?: Rule<TValues, z.ZodTypeAny | undefined>;
		onBlurAsync?: Rule<TValues, z.ZodTypeAny | undefined>;
	};
};

export type FieldRule<TValues> = BuiltInRule<TValues> | SchemaRule<TValues> | GranularRule<TValues>;

function isSchemaRule<TValues>(rule: FieldRule<TValues>): rule is SchemaRule<TValues> {
	return "schema" in rule;
}

function isGranularRule<TValues>(rule: FieldRule<TValues>): rule is GranularRule<TValues> {
	return "validators" in rule;
}

function hasNoTypeRule(rules: BuiltInRule<any>): boolean {
	return !rules.isDate && !rules.isEmail && !rules.isNumber && !rules.max && !rules.min;
}

export type Rules = {
	asyncDebounceMs?: number;
	onChange?: AnySchema;
	onChangeAsync?: AnySchema;
	onBlur?: AnySchema;
	onBlurAsync?: AnySchema;
};

export function useFieldRule<TValues extends GenericValues>(
	id: InternalFormApiId<TValues>,
	options?: MaybeRefOrGetter<FieldRule<TValues>>
): ComputedRef<Rules | undefined> {
	const { values } = useInternalFormApi(id);

	function evalRule<TRule>(rule: Rule<TValues, TRule>): TRule | undefined {
		if (rule instanceof Function) {
			return rule(values.value as any);
		}

		return toValue(rule);
	}

	function buildDefaultRule(rules: BuiltInRule<TValues>): z.ZodTypeAny | undefined {
		let schema: z.ZodTypeAny;

		const min = evalRule(rules.min),
			max = evalRule(rules.max),
			isRequired = evalRule(rules.isRequired),
			isNumber = evalRule(rules.isNumber),
			isDate = evalRule(rules.isDate),
			isEmail = evalRule(rules.isEmail);

		if (hasNoTypeRule(rules)) {
			if (isRequired != null)
				return z.any().superRefine((val, ctx) => {
					if (isRequired && (val == null || val === "")) {
						ctx.addIssue({
							code: z.ZodIssueCode.invalid_type,
							received: z.ZodParsedType.undefined,

							// Make ts happy
							expected: z.ZodParsedType.unknown,
						});
					}
				});

			return undefined;
		}

		if (isNumber) {
			let numberSchema = z.number({ errorMap: rules.errorMap });
			if (min != null) {
				numberSchema = numberSchema.min(min.valueOf());
			}
			if (max != null) {
				numberSchema = numberSchema.max(max.valueOf());
			}

			schema = numberSchema;
		} else if (isDate) {
			let dateSchema = z.date({ errorMap: rules.errorMap });
			if (min != null) {
				dateSchema = dateSchema.min(new Date(min.valueOf()));
			}
			if (max != null) {
				dateSchema = dateSchema.max(new Date(max.valueOf()));
			}

			schema = dateSchema;
		} else {
			let stringSchema = z.string({ errorMap: rules.errorMap });
			if (min != null) {
				stringSchema = stringSchema.min(min.valueOf());
			}
			if (max != null) {
				stringSchema = stringSchema.max(max.valueOf());
			}
			if (isEmail) {
				stringSchema = stringSchema.email();
			}
			if (!isRequired) {
				schema = z.union([stringSchema, z.literal("")]);
			} else {
				schema = stringSchema;
			}
		}

		if (!isRequired) {
			schema = schema.optional();
		}

		return schema;
	}

	function buildRule(rules: FieldRule<TValues>): Rules {
		if (isSchemaRule(rules)) {
			return { onChange: evalRule(rules.schema) };
		}

		if (isGranularRule(rules)) {
			return {
				asyncDebounceMs: rules.asyncDebounceMs,
				onChange: evalRule(rules.validators.onChange),
				onChangeAsync: evalRule(rules.validators.onChangeAsync),
				onBlur: evalRule(rules.validators.onBlur),
				onBlurAsync: evalRule(rules.validators.onBlurAsync),
			};
		}

		return { onChange: buildDefaultRule(rules) };
	}

	const schema = computed(() => buildRule(toValue(options) || {}));

	return schema;
}
